// ============================================================================
// AI ERP ASSISTANT - Financial Management Assistant
// Chat-based interface for invoice analysis, payment tracking, cash flow insights
// Uses unified /api/v1/ai-assistant endpoints with moduleContext='finance'
// Date: January 2026
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-finance/administration.mdx
 * (published at user-docs.totalaccess.co.za/total-finance/administration)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Receipt,
  TrendingUp,
  TrendingDown,
  Calendar,
  Users,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Zap,
  ArrowRight,
  Clock,
  DollarSign,
  CreditCard,
  BarChart3,
  FileText,
  PieChart,
  Target,
  Wallet,
  BanknoteIcon,
  Calculator,
  Plus,
  Send,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  History,
  Copy,
  ShieldAlert,
  GraduationCap
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAIAssistant } from '@/contexts/AIAssistantContext';
import { useModuleColor } from '@/hooks/useModuleColor';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';

import type { DocumentType } from '@/hooks/useDocumentsWorkItems';
import AIAssistantChat from '@/components/shared/AIAssistantChat';
import type { AIMessage, AIAttachment, AISuggestedAction, QuickAction, ExamplePrompt } from '@/components/shared/AIAssistantChat';
import UserDocsLink from '@/components/shared/UserDocsLink';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface AIERPAssistantProps {
  permissions: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
  contextInvoiceId?: string;
  contextContactId?: string;
  activeSecondaryTab?: string | null;
  contextDocument?: FinanceDocumentAssistantContext | null;
  embedded?: boolean;
  hideEmbeddedHeader?: boolean;
  onClose?: () => void;
  initialPrompt?: string;
  /** When true, renders as a floating bottom-right panel (blue border) like the GuidedTour chat. */
  floatingPanel?: boolean;
}

export interface FinanceDocumentAssistantLineItem {
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
  taxRate?: number | null;
  lineType?: string | null;
  itemCode?: string | null;
}

export interface FinanceDocumentAssistantPromotionTarget {
  type: DocumentType;
  label: string;
}

export interface FinanceDocumentAssistantContext {
  documentId?: string | null;
  documentNumber?: string | null;
  documentType: DocumentType;
  title?: string | null;
  status?: string | null;
  paymentStatus?: string | null;
  contactId?: number | null;
  contactName?: string | null;
  contactEmail?: string | null;
  currency?: string | null;
  issuedAt?: string | null;
  dueDate?: string | null;
  locationName?: string | null;
  contactPersonName?: string | null;
  subtotal?: number | null;
  taxTotal?: number | null;
  grandTotal?: number | null;
  amountPaid?: number | null;
  balanceDue?: number | null;
  notes?: string | null;
  internalNotes?: string | null;
  lineItemCount?: number;
  lineItems?: FinanceDocumentAssistantLineItem[];
  parentDocumentType?: DocumentType | null;
  parentDocumentNumber?: string | null;
  availablePromotions?: FinanceDocumentAssistantPromotionTarget[];
  attentionFlags?: string[];
  // Expense-specific context fields
  anomalyFlags?: string[];
  linkedPurchaseOrderId?: string | null;
  linkedPurchaseOrderNumber?: string | null;
  threeWayMatchStatus?: string | null;
  exchangeRate?: number | null;
  baseAmount?: number | null;
  exchangeRateDate?: string | null;
  policyViolations?: string[];
  semanticDuplicates?: Array<{ work_item_id: number; vendor_name: string | null; total_amount: number | null; similarity: number }>;
  approvedGLAllocations?: Array<{
    work_item_id: number;
    vendor_name: string | null;
    expense_account_code: string | null;
    matched_contact_id: number | null;
    matched_supplier_name: string | null;
    similarity: number;
  }>;
  // Risk & fraud detection fields
  riskFlags?: Array<{
    id: string;
    category: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    detail: string;
    recommendation: string;
    requiresAcknowledgement: boolean;
  }>;
  riskSummary?: string;
  hasBlockingRisk?: boolean;
}

interface InvoiceInsight {
  invoiceId: string;
  invoiceNumber: string;
  contactName: string;
  amount: number;
  balanceDue: number;
  daysPastDue: number;
  paymentStatus: 'pending' | 'partial' | 'paid' | 'overdue';
  riskLevel: 'low' | 'medium' | 'high';
  recommendations?: string[];
  lastPaymentDate?: string;
}

interface CashFlowSummary {
  period: string;
  inflows: number;
  outflows: number;
  netCashFlow: number;
  trend: 'improving' | 'stable' | 'declining';
  projectedBalance: number;
}

interface PaymentAnalysis {
  customerId: string;
  customerName: string;
  totalInvoices: number;
  paidOnTime: number;
  avgDaysToPay: number;
  totalOutstanding: number;
  creditRisk: 'low' | 'medium' | 'high';
  paymentHistory: string;
}

interface CashFlowForecast {
  period: string;
  projectedInflows: number;
  projectedOutflows: number;
  projectedNet: number;
  confidence: 'high' | 'medium' | 'low';
  keyDrivers: string[];
  riskFactors: string[];
}

interface TransactionAnomaly {
  transactionId: string;
  transactionNumber: string;
  amount: number;
  expectedAmount: number;
  deviation: number;
  anomalyType: 'high_amount' | 'unusual_category' | 'duplicate' | 'timing' | 'vendor_change';
  severity: 'low' | 'medium' | 'high';
  description: string;
  recommendation: string;
}

interface RevenueTrend {
  period: string;
  revenue: number;
  growthRate: number;
  seasonalityFactor: number;
  forecast: number;
  trend: 'increasing' | 'stable' | 'decreasing';
}

interface ExpenseCategorization {
  expenseId: string;
  description: string;
  amount: number;
  suggestedCategory: string;
  confidence: number;
  alternativeCategories: Array<{
    category: string;
    confidence: number;
  }>;
  reasoning: string;
}

interface FinancialKpis {
  revenue?: number;
  outstanding?: number;
  overdueCount?: number;
  collectionRate?: number;
}

interface AssistantProfile {
  title: string;
  subtitle: string;
  welcomeTitle: string;
  welcomeMessage: string;
  inputPlaceholder: string;
  quickActions: QuickAction[];
  examplePrompts: ExamplePrompt[];
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const BASE_EXAMPLE_PROMPTS: ExamplePrompt[] = [
  { icon: AlertTriangle, text: '🚨 What in finance needs attention first today?', category: 'Priorities' },
  { icon: Receipt, text: '📊 Show the highest-risk overdue balances and next actions', category: 'Collections' },
  { icon: TrendingUp, text: '📈 Give me a concise cash flow and receivables summary', category: 'Cash Flow' },
  { icon: Users, text: '👥 Which customers or suppliers need follow-up today?', category: 'Stakeholders' },
  { icon: DollarSign, text: '💰 Summarise month-to-date revenue, payables, and collections', category: 'Month-End' },
  { icon: Lightbulb, text: '💡 Suggest practical actions to improve collections and cash timing', category: 'Insights' },
  { icon: Target, text: '🎯 Forecast cash flow for the next 30 days with key drivers', category: 'Forecasting' },
  { icon: Calculator, text: '🧮 Detect unusual transactions and anomalies in recent expenses', category: 'Anomaly Detection' },
  { icon: BarChart3, text: '📉 Analyze revenue trends with seasonality and growth patterns', category: 'Trends' },
  { icon: FileText, text: '📋 Suggest expense categories for uncategorized transactions', category: 'Categorization' },
];

const BASE_QUICK_ACTIONS: QuickAction[] = [
  { id: 'finance_priorities', label: 'Needs Attention', icon: AlertTriangle, prompt: 'What in finance needs attention first right now, and why?' },
  { id: 'overdue', label: 'Overdue', icon: Receipt, prompt: 'List the overdue invoices or balances that need immediate collection attention.' },
  { id: 'cash_flow', label: 'Cash Flow', icon: TrendingUp, prompt: 'Give me a concise cash flow summary for the current period, including likely pressure points.' },
  { id: 'top_debtors', label: 'Top Debtors', icon: Users, prompt: 'Who are the top customers by outstanding balance, with payment risk notes and follow-up priorities?' },
  { id: 'payables', label: 'Payables', icon: CreditCard, prompt: 'Summarise supplier bills or payables that need scheduling, approval, or escalation.' },
  { id: 'forecast', label: 'Forecast', icon: Target, prompt: 'Generate a cash flow forecast for the next 30 days with confidence levels and key risk factors.' },
  { id: 'anomalies', label: 'Anomalies', icon: Calculator, prompt: 'Detect and analyze unusual transactions or anomalies in recent financial data with recommendations.' },
  { id: 'trends', label: 'Trends', icon: BarChart3, prompt: 'Analyze revenue and expense trends with seasonality factors and AI-powered forecasts.' },
  { id: 'categorize', label: 'Categorize', icon: FileText, prompt: 'Suggest expense categories for uncategorized transactions with confidence scores and reasoning.' }
];

const PURCHASE_DOCUMENT_TYPES = new Set<DocumentType>([
  'purchase_order',
  'expense',
  'grn',
  'supplier_credit',
  'supplier_quote',
  'purchase_requisition',
  'rfq',
  'purchase_return',
]);

function humanizeLabel(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatContextDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatMoney(value: number | null | undefined, currency = 'ZAR'): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  try {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function truncateText(value: string | null | undefined, maxLength = 320): string | undefined {
  const normalized = value?.trim();
  if (!normalized) return undefined;
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}…`;
}

function joinHumanList(values: Array<string | null | undefined>, conjunction = 'and'): string {
  const filtered = values.filter((value): value is string => Boolean(value?.trim()));
  if (filtered.length === 0) return '';
  if (filtered.length === 1) return filtered[0];
  if (filtered.length === 2) return `${filtered[0]} ${conjunction} ${filtered[1]}`;
  return `${filtered.slice(0, -1).join(', ')}, ${conjunction} ${filtered[filtered.length - 1]}`;
}

function shortenPromotionLabel(label: string): string {
  return label
    .replace(/^Convert to\s+/i, '')
    .replace(/^Generate\s+/i, '')
    .replace(/^Create\s+/i, '')
    .trim();
}

function buildFinanceAssistantProfile({
  activeSecondaryTab,
  contextDocument,
}: {
  activeSecondaryTab?: string | null;
  contextDocument?: FinanceDocumentAssistantContext | null;
}): AssistantProfile {
  if (contextDocument) {
    const docLabel = humanizeLabel(contextDocument.documentType);
    const identifier = contextDocument.documentNumber || contextDocument.title || docLabel;
    const balanceSummary = formatMoney(contextDocument.balanceDue, contextDocument.currency || 'ZAR');
    const totalSummary = formatMoney(contextDocument.grandTotal, contextDocument.currency || 'ZAR');
    const dueSummary = formatContextDate(contextDocument.dueDate);
    const statusLabel = contextDocument.status ? humanizeLabel(contextDocument.status) : 'Draft';
    const counterpartyRole = PURCHASE_DOCUMENT_TYPES.has(contextDocument.documentType) ? 'supplier' : 'customer';
    const statusValue = (contextDocument.status || 'draft').toLowerCase();
    const isDraft = statusValue === 'draft';
    const isPendingApproval = statusValue.includes('pending');
    const isSentOrInFlight = ['sent', 'viewed', 'partially_paid', 'overdue'].includes(statusValue);
    const hasOutstandingBalance = typeof contextDocument.balanceDue === 'number' && contextDocument.balanceDue > 0.009;
    const dueDate = contextDocument.dueDate ? new Date(contextDocument.dueDate) : null;
    const isPastDue = Boolean(
      dueDate
      && !Number.isNaN(dueDate.getTime())
      && hasOutstandingBalance
      && dueDate.getTime() < Date.now()
    );
    const availablePromotionLabels = (contextDocument.availablePromotions || []).map((target) => shortenPromotionLabel(target.label));
    const workflowSummary = availablePromotionLabels.length > 0
      ? `Next: ${joinHumanList(availablePromotionLabels, 'or')}`
      : null;
    const attentionHighlights = (contextDocument.attentionFlags || []).slice(0, 3);
    const attentionSummary = attentionHighlights.length > 0
      ? `Focus on ${joinHumanList(attentionHighlights, 'and')}.`
      : '';
    const lineageSummary = contextDocument.parentDocumentNumber
      ? `Linked from ${humanizeLabel(contextDocument.parentDocumentType)} ${contextDocument.parentDocumentNumber}.`
      : '';
    const isExpense = contextDocument.documentType === 'expense';
    const poSummary = isExpense && contextDocument.linkedPurchaseOrderNumber
      ? `Linked to PO ${contextDocument.linkedPurchaseOrderNumber}`
      : null;
    const threeWayMatchSummary = isExpense && contextDocument.threeWayMatchStatus
      ? `Three-way match: ${humanizeLabel(contextDocument.threeWayMatchStatus)}`
      : null;
    const fxSummary = isExpense && contextDocument.exchangeRate && contextDocument.baseAmount
      ? `FX ${contextDocument.exchangeRate} → base ${formatMoney(contextDocument.baseAmount, 'ZAR')}`
      : null;
    const anomalySummary = isExpense && (contextDocument.anomalyFlags || []).length > 0
      ? `Anomaly flags: ${joinHumanList(contextDocument.anomalyFlags!.slice(0, 3), 'and')}`
      : null;
    const policySummary = isExpense && (contextDocument.policyViolations || []).length > 0
      ? `Policy violations: ${joinHumanList(contextDocument.policyViolations!.slice(0, 3), 'and')}`
      : null;
    const duplicateSummary = isExpense && (contextDocument.semanticDuplicates || []).length > 0
      ? `${contextDocument.semanticDuplicates!.length} potential duplicate${contextDocument.semanticDuplicates!.length === 1 ? '' : 's'} detected`
      : null;
    const glAllocationSummary = isExpense && (contextDocument.approvedGLAllocations || []).length > 0
      ? `${contextDocument.approvedGLAllocations!.length} similar past expense${contextDocument.approvedGLAllocations!.length === 1 ? '' : 's'} with GL allocations found`
      : null;
    const riskFlags = contextDocument.riskFlags || [];
    const highRiskFlags = riskFlags.filter((f) => f.severity === 'high' || f.severity === 'critical');
    const riskSummaryLine = riskFlags.length > 0
      ? `Risk: ${highRiskFlags.length > 0 ? `${highRiskFlags.length} high-risk` : `${riskFlags.length} flag${riskFlags.length === 1 ? '' : 's'}`} detected`
      : null;
    const riskWelcomeLine = riskFlags.length > 0
      ? ` ${riskFlags.length} risk indicator${riskFlags.length === 1 ? '' : 's'} detected${highRiskFlags.length > 0 ? ` (${highRiskFlags.length} high severity — review required)` : ''}.`
      : '';
    const readinessLabel = isDraft
      ? 'Send Ready?'
      : isPendingApproval
        ? 'Approval Ready?'
        : 'Status Check';
    const readinessPrompt = isDraft
      ? `Is this ${docLabel.toLowerCase()} ready to send or share, and what should be fixed first?`
      : isPendingApproval
        ? `Is this ${docLabel.toLowerCase()} ready for approval, and what could still block approval or payment?`
        : `Check whether this ${docLabel.toLowerCase()} matches its current status, payment state, and next workflow step.`;
    const workflowLabel = availablePromotionLabels.length > 0 ? 'Conversion Path' : 'Next Step';
    const workflowPrompt = availablePromotionLabels.length > 0
      ? `What is the best next workflow step for this ${docLabel.toLowerCase()}, and is it ready to move to ${joinHumanList(availablePromotionLabels, 'or')}?`
      : `What is the next best workflow step for this ${docLabel.toLowerCase()} from its current status, and what should happen before that?`;
    const riskLabel = hasOutstandingBalance || isSentOrInFlight ? 'Payment Risk' : 'Commercial Risk';
    const riskPrompt = hasOutstandingBalance || isSentOrInFlight
      ? `Assess the payment, approval, or collection risk on this ${docLabel.toLowerCase()} and recommend practical next steps.`
      : `Assess the commercial and workflow risk on this ${docLabel.toLowerCase()}, including missing details, pricing, and approval readiness.`;
    const communicationLabel = PURCHASE_DOCUMENT_TYPES.has(contextDocument.documentType)
      ? isPendingApproval
        ? 'Draft Approval Note'
        : hasOutstandingBalance
          ? 'Draft Supplier Follow-up'
          : 'Draft Supplier Note'
      : isPastDue
        ? 'Draft Collection Note'
        : hasOutstandingBalance
          ? 'Draft Payment Follow-up'
          : isDraft
            ? 'Draft Send Note'
            : 'Draft Customer Note';
    const communicationPrompt = PURCHASE_DOCUMENT_TYPES.has(contextDocument.documentType)
      ? `Draft a concise supplier-ready note for this ${docLabel.toLowerCase()}, using the current status, total, due date, and any approval or payment follow-up needed.`
      : isPastDue
        ? `Draft a polite but firm collection follow-up for this ${docLabel.toLowerCase()}, including the outstanding balance, due date, and next action.`
        : hasOutstandingBalance
          ? `Draft a customer payment follow-up for this ${docLabel.toLowerCase()}, using the outstanding balance, due date, and the most helpful next step.`
          : `Draft a concise customer-ready message for this ${docLabel.toLowerCase()}, using the current status, total, due date, and any likely follow-up needed.`;

    const expenseQuickActions: QuickAction[] = isExpense ? ([
      contextDocument.linkedPurchaseOrderNumber ? {
        id: 'verify_po_match',
        label: 'Verify PO Match',
        icon: CheckCircle,
        prompt: `Verify the three-way match for this expense linked to PO ${contextDocument.linkedPurchaseOrderNumber}. Check whether quantities, prices, and totals are consistent between the expense, PO, and any GRN. Flag any discrepancies.`,
      } : null,
      contextDocument.exchangeRate ? {
        id: 'check_fx_rate',
        label: 'Check FX Rate',
        icon: DollarSign,
        prompt: `Check the exchange rate of ${contextDocument.exchangeRate} applied to this expense${contextDocument.baseAmount ? ` (base amount ${formatMoney(contextDocument.baseAmount, 'ZAR')})` : ''}. Is the rate reasonable for the date and currency pair? What is the risk of using this rate?`,
      } : null,
      {
        id: 'suggest_gl_coding',
        label: 'Suggest GL Coding',
        icon: Calculator,
        prompt: `Suggest the best GL account coding for this expense based on the vendor, line item descriptions, and amounts. Provide the account code, name, and reasoning for each line item.`,
      },
      (contextDocument.policyViolations || []).length > 0 ? {
        id: 'policy_check',
        label: 'Policy Check',
        icon: AlertTriangle,
        prompt: `Review the policy violations on this expense: ${joinHumanList(contextDocument.policyViolations!, 'and')}. Explain each violation, whether it is a blocker or warning, and what action is needed to resolve it.`,
      } : null,
      (contextDocument.approvedGLAllocations || []).length > 0 ? {
        id: 'suggest_allocation_from_history',
        label: 'Suggest GL from History',
        icon: Calculator,
        prompt: `Based on ${contextDocument.approvedGLAllocations!.length} similar past expense${contextDocument.approvedGLAllocations!.length === 1 ? '' : 's'} with approved GL allocations, suggest the best account coding for this expense. Reference the historical GL codes and explain why they apply.`,
      } : null,
    ] as (QuickAction | null)[]).filter((a): a is QuickAction => a !== null) : [];

    return {
      title: 'AI Assistant - Finance',
      subtitle: [
        docLabel,
        identifier,
        contextDocument.contactName || null,
        statusLabel,
        balanceSummary ? `Balance ${balanceSummary}` : totalSummary ? `Total ${totalSummary}` : null,
        workflowSummary,
        poSummary,
        threeWayMatchSummary,
        fxSummary,
        anomalySummary,
        policySummary,
        duplicateSummary,
        glAllocationSummary,
        riskSummaryLine,
      ].filter(Boolean).join(' • '),
      welcomeTitle: `Review ${identifier}`,
      welcomeMessage: `I'm focused on this ${docLabel.toLowerCase()}${contextDocument.contactName ? ` for ${contextDocument.contactName}` : ''}. I can help spot missing commercial details, payment or approval risk, document consistency issues, and the best next step in the workflow.${dueSummary ? ` Due ${dueSummary}.` : ''}${balanceSummary ? ` Outstanding balance ${balanceSummary}.` : totalSummary ? ` Document total ${totalSummary}.` : ''}${contextDocument.lineItemCount ? ` ${contextDocument.lineItemCount} line item${contextDocument.lineItemCount === 1 ? '' : 's'} loaded.` : ''}${lineageSummary ? ` ${lineageSummary}` : ''}${workflowSummary ? ` ${workflowSummary}.` : ''}${attentionSummary ? ` ${attentionSummary}` : ''}${poSummary ? ` ${poSummary}.` : ''}${threeWayMatchSummary ? ` ${threeWayMatchSummary}.` : ''}${fxSummary ? ` ${fxSummary}.` : ''}${anomalySummary ? ` ${anomalySummary}.` : ''}${policySummary ? ` ${policySummary}.` : ''}${duplicateSummary ? ` ${duplicateSummary}.` : ''}${glAllocationSummary ? ` ${glAllocationSummary}.` : ''}${riskWelcomeLine}`,
      inputPlaceholder: `Ask me to review this ${docLabel.toLowerCase()}...`,
      quickActions: [
        {
          id: 'review_document',
          label: 'Review Document',
          icon: FileText,
          prompt: `Review this ${docLabel.toLowerCase()}${contextDocument.documentNumber ? ` (${contextDocument.documentNumber})` : ''} for missing information, financial risk, and the next best action.`,
        },
        {
          id: 'readiness_check',
          label: readinessLabel,
          icon: CheckCircle,
          prompt: readinessPrompt,
        },
        {
          id: 'missing_info',
          label: 'Missing Info',
          icon: AlertTriangle,
          prompt: `What is missing or inconsistent in this ${docLabel.toLowerCase()} before it is sent, approved, paid, or converted?`,
        },
        {
          id: 'risk_assessment',
          label: riskLabel,
          icon: DollarSign,
          prompt: riskPrompt,
        },
        riskFlags.length > 0 ? {
          id: 'fraud_risk_review',
          label: 'Review Risk Flags',
          icon: ShieldAlert,
          prompt: `The following risk indicators were detected for this ${docLabel.toLowerCase()}:
${riskFlags.map((f) => `- [${f.severity.toUpperCase()}] ${f.title}: ${f.detail} → ${f.recommendation}`).join('\n')}

Assess these risks and recommend whether to proceed, request additional verification, or hold the document.`,
        } as QuickAction : null,
        {
          id: 'workflow_next_step',
          label: workflowLabel,
          icon: ArrowRight,
          prompt: workflowPrompt,
        },
        {
          id: 'draft_message',
          label: communicationLabel,
          icon: Send,
          prompt: communicationPrompt,
        },
        ...expenseQuickActions,
      ].filter((a): a is QuickAction => a !== null),
      examplePrompts: [
        {
          icon: FileText,
          text: `📋 Review this ${docLabel.toLowerCase()} for missing details, financial risk, and next best action`,
          category: 'Document Review',
        },
        {
          icon: DollarSign,
          text: hasOutstandingBalance || isSentOrInFlight
            ? `💰 Summarise the balance, due date, collection risk, and best follow-up for this ${docLabel.toLowerCase()}`
            : `💰 Summarise the total, terms, and financial risk on this ${docLabel.toLowerCase()}`,
          category: riskLabel,
        },
        {
          icon: CheckCircle,
          text: isDraft || isPendingApproval
            ? `✅ Check whether this ${docLabel.toLowerCase()} is ready for ${isPendingApproval ? 'approval' : 'sending'} and what is still missing`
            : `✅ Check whether the line items, totals, tax, and terms look internally consistent`,
          category: isDraft || isPendingApproval ? 'Readiness' : 'Consistency Check',
        },
        {
          icon: ArrowRight,
          text: availablePromotionLabels.length > 0
            ? `➡️ Should this ${docLabel.toLowerCase()} move to ${joinHumanList(availablePromotionLabels, 'or')} next, and what is still missing?`
            : `➡️ What is the next best workflow step for this ${docLabel.toLowerCase()}?`,
          category: workflowLabel,
        },
        {
          icon: Send,
          text: `✉️ ${communicationPrompt}`,
          category: 'Communication',
        },
      ],
    };
  }

  const focusArea = activeSecondaryTab ? humanizeLabel(activeSecondaryTab) : null;

  return {
    title: 'AI Assistant - Accounts',
    subtitle: focusArea ? `Focused on ${focusArea}` : 'Powered by Total Access',
    welcomeTitle: 'How can I help with finance?',
    welcomeMessage: `I can help you analyse invoices, bills, expenses, cash flow, collections, and payables.${focusArea ? ` I can also focus on ${focusArea.toLowerCase()} right now.` : ''} Ask for risks, priorities, aging, month-end tasks, or a concise summary of what needs attention first.`,
    inputPlaceholder: 'Ask about invoices, bills, expenses, cash flow, collections, or payables...',
    quickActions: BASE_QUICK_ACTIONS,
    examplePrompts: BASE_EXAMPLE_PROMPTS,
  };
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function InvoiceInsightCard({ insight }: { insight: InvoiceInsight }) {
  const [expanded, setExpanded] = useState(false);
  const recommendations = Array.isArray(insight.recommendations) ? insight.recommendations : [];

  const statusColor = {
    paid: 'text-green-600 bg-green-50 dark:bg-green-900/30',
    pending: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30',
    partial: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30',
    overdue: 'text-red-600 bg-red-50 dark:bg-red-900/30'
  }[insight.paymentStatus];

  const riskColor = {
    low: 'text-green-600',
    medium: 'text-amber-600',
    high: 'text-red-600'
  }[insight.riskLevel];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="w-4 h-4 text-teal-600" />
              <span className="text-xs text-gray-500 font-mono">{insight.invoiceNumber}</span>
            </div>
            <h4 className="font-medium text-gray-900 dark:text-white text-sm">{insight.contactName}</h4>
            <div className="flex items-center gap-3 text-xs mt-2">
              <span className={`px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
                {insight.paymentStatus}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                R{insight.amount.toLocaleString()}
              </span>
              {insight.balanceDue > 0 && (
                <span className="text-red-600 font-medium">
                  Due: R{insight.balanceDue.toLocaleString()}
                </span>
              )}
              {insight.daysPastDue > 0 && (
                <span className="text-red-500">
                  {insight.daysPastDue} days overdue
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${riskColor}`}>
              {insight.riskLevel} risk
            </span>
            <button className="p-1">
              {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
          </div>
        </div>
      </div>

      {expanded && recommendations.length > 0 && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-3">
          <h5 className="text-xs font-medium text-teal-600 mb-2 flex items-center gap-1">
            <Lightbulb className="w-3 h-3" /> Recommendations
          </h5>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            {recommendations.map((rec, i) => (
              <li key={i} className="flex items-start gap-1">
                <span className="text-teal-400">•</span> {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CashFlowCard({ summary }: { summary: CashFlowSummary }) {
  const trendIcon = summary.trend === 'improving' 
    ? <TrendingUp className="w-4 h-4 text-green-500" />
    : summary.trend === 'declining'
      ? <TrendingDown className="w-4 h-4 text-red-500" />
      : <ArrowRight className="w-4 h-4 text-gray-400" />;

  const netColor = summary.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-teal-600" />
          <h4 className="font-medium text-gray-900 dark:text-white text-sm">Cash Flow: {summary.period}</h4>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          {trendIcon}
          <span>{summary.trend}</span>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-lg font-bold text-green-600">R{summary.inflows.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Inflows</p>
        </div>
        <div>
          <p className="text-lg font-bold text-red-600">R{summary.outflows.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Outflows</p>
        </div>
        <div>
          <p className={`text-lg font-bold ${netColor}`}>R{summary.netCashFlow.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Net</p>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Projected Balance</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            R{summary.projectedBalance.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

function PaymentAnalysisCard({ analysis }: { analysis: PaymentAnalysis }) {
  const riskBadge = {
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  }[analysis.creditRisk];

  const paymentScore = analysis.totalInvoices > 0
    ? Math.round((analysis.paidOnTime / analysis.totalInvoices) * 100)
    : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-teal-600" />
          <h4 className="font-medium text-gray-900 dark:text-white text-sm">{analysis.customerName}</h4>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${riskBadge}`}>
          {analysis.creditRisk} risk
        </span>
      </div>
      
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-500">Payment Score</span>
          <span className={`font-bold ${paymentScore >= 80 ? 'text-green-600' : paymentScore >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
            {paymentScore}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full ${
              paymentScore >= 80 ? 'bg-green-500' : 
              paymentScore >= 50 ? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${paymentScore}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="text-gray-500">Invoices</span>
          <p className="font-semibold text-gray-900 dark:text-white">{analysis.totalInvoices}</p>
        </div>
        <div>
          <span className="text-gray-500">Avg Days to Pay</span>
          <p className="font-semibold text-gray-900 dark:text-white">{analysis.avgDaysToPay} days</p>
        </div>
        <div className="col-span-2">
          <span className="text-gray-500">Outstanding</span>
          <p className="font-semibold text-red-600">R{analysis.totalOutstanding.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

function FinancialKPICard({ kpis }: { kpis: FinancialKpis }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-teal-600" />
        <h4 className="font-medium text-gray-900 dark:text-white text-sm">Financial KPIs</h4>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">R{(kpis.revenue || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-500">Revenue (MTD)</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-red-600">R{(kpis.outstanding || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-500">Outstanding</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-600">{kpis.overdueCount || 0}</p>
          <p className="text-xs text-gray-500">Overdue Invoices</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-teal-600">{kpis.collectionRate || 0}%</p>
          <p className="text-xs text-gray-500">Collection Rate</p>
        </div>
      </div>
    </div>
  );
}

function FinanceActionsCard({ actions, onApply }: { 
  actions: AISuggestedAction[]; 
  onApply: (action: AISuggestedAction) => void;
}) {
  const getActionIcon = (type: string) => {
    switch (type) {
      case 'create_invoice': return <Plus className="w-4 h-4" />;
      case 'send_reminder': return <Send className="w-4 h-4" />;
      case 'record_payment': return <DollarSign className="w-4 h-4" />;
      case 'flag_overdue': return <AlertTriangle className="w-4 h-4" />;
      case 'update_status': return <CheckCircle className="w-4 h-4" />;
      case 'generate_report': return <BarChart3 className="w-4 h-4" />;
      case 'credit_hold': return <CreditCard className="w-4 h-4" />;
      case 'open_coach': return <GraduationCap className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const getActionColor = (type: string) => {
    switch (type) {
      case 'create_invoice': return 'bg-green-100 dark:bg-green-900/30 text-green-600';
      case 'send_reminder': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600';
      case 'record_payment': return 'bg-teal-100 dark:bg-teal-900/30 text-teal-600';
      case 'flag_overdue': return 'bg-red-100 dark:bg-red-900/30 text-red-600';
      case 'credit_hold': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600';
      case 'open_coach': return 'bg-red-100 dark:bg-red-900/30 text-red-600';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-3 border-b border-gray-100 dark:border-gray-700 bg-teal-50 dark:bg-teal-900/30">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-teal-600" />
          <span className="font-medium text-sm text-gray-900 dark:text-white">Recommended Actions</span>
        </div>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {actions.map((action) => (
          <div key={action.id} className="p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${getActionColor(action.type)}`}>
                {getActionIcon(action.type)}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{action.label}</p>
              </div>
            </div>
            <button
              onClick={() => onApply(action)}
              disabled={action.applied}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                action.applied
                  ? 'bg-green-100 text-green-600 cursor-default'
                  : action.type === 'open_coach'
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
              }`}
            >
              {action.applied ? 'Done' : action.type === 'open_coach' ? 'Open Coach' : 'Apply'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CashFlowForecastCard({ forecast }: { forecast: CashFlowForecast }) {
  const confidenceColor = {
    high: 'text-green-600 bg-green-50 dark:bg-green-900/30',
    medium: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30',
    low: 'text-red-600 bg-red-50 dark:bg-red-900/30'
  }[forecast.confidence];

  const netColor = forecast.projectedNet >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-teal-600" />
          <h4 className="font-medium text-gray-900 dark:text-white text-sm">Cash Flow Forecast: {forecast.period}</h4>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${confidenceColor}`}>
          {forecast.confidence} confidence
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-4 text-center mb-4">
        <div>
          <p className="text-lg font-bold text-green-600">R{forecast.projectedInflows.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Projected Inflows</p>
        </div>
        <div>
          <p className="text-lg font-bold text-red-600">R{forecast.projectedOutflows.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Projected Outflows</p>
        </div>
        <div>
          <p className={`text-lg font-bold ${netColor}`}>R{forecast.projectedNet.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Projected Net</p>
        </div>
      </div>

      {forecast.keyDrivers.length > 0 && (
        <div className="mb-3">
          <h5 className="text-xs font-medium text-teal-600 mb-2 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Key Drivers
          </h5>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            {forecast.keyDrivers.map((driver, i) => (
              <li key={i} className="flex items-start gap-1">
                <span className="text-green-400">•</span> {driver}
              </li>
            ))}
          </ul>
        </div>
      )}

      {forecast.riskFactors.length > 0 && (
        <div>
          <h5 className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Risk Factors
          </h5>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            {forecast.riskFactors.map((risk, i) => (
              <li key={i} className="flex items-start gap-1">
                <span className="text-red-400">•</span> {risk}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TransactionAnomalyCard({ anomaly }: { anomaly: TransactionAnomaly }) {
  const severityColor = {
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  }[anomaly.severity];

  const anomalyIcon = {
    high_amount: <DollarSign className="w-4 h-4" />,
    unusual_category: <FileText className="w-4 h-4" />,
    duplicate: <Copy className="w-4 h-4" />,
    timing: <Clock className="w-4 h-4" />,
    vendor_change: <Users className="w-4 h-4" />
  }[anomaly.anomalyType];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600">
            {anomalyIcon}
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white text-sm">{anomaly.transactionNumber}</h4>
            <p className="text-xs text-gray-500">{anomaly.anomalyType.replace('_', ' ')}</p>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityColor}`}>
          {anomaly.severity} severity
        </span>
      </div>
      
      <div className="mb-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Amount</span>
          <span className="font-semibold text-gray-900 dark:text-white">R{anomaly.amount.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Expected</span>
          <span className="font-semibold text-gray-900 dark:text-white">R{anomaly.expectedAmount.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Deviation</span>
          <span className={`font-semibold ${Math.abs(anomaly.deviation) > 50 ? 'text-red-600' : 'text-amber-600'}`}>
            {anomaly.deviation > 0 ? '+' : ''}{anomaly.deviation.toFixed(1)}%
          </span>
        </div>
      </div>

      <div className="mb-3">
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{anomaly.description}</p>
      </div>

      <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-xs font-medium text-blue-700 dark:text-blue-300 flex items-start gap-1">
          <Lightbulb className="w-3 h-3 flex-shrink-0 mt-0.5" />
          {anomaly.recommendation}
        </p>
      </div>
    </div>
  );
}

function RevenueTrendCard({ trend }: { trend: RevenueTrend }) {
  const trendIcon = trend.trend === 'increasing' 
    ? <TrendingUp className="w-4 h-4 text-green-500" />
    : trend.trend === 'decreasing'
      ? <TrendingDown className="w-4 h-4 text-red-500" />
      : <ArrowRight className="w-4 h-4 text-gray-400" />;

  const trendColor = trend.trend === 'increasing' ? 'text-green-600' : trend.trend === 'decreasing' ? 'text-red-600' : 'text-gray-600';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-teal-600" />
          <h4 className="font-medium text-gray-900 dark:text-white text-sm">Revenue Trend: {trend.period}</h4>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          {trendIcon}
          <span className={trendColor}>{trend.trend}</span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-center mb-4">
        <div>
          <p className="text-lg font-bold text-teal-600">R{trend.revenue.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Actual Revenue</p>
        </div>
        <div>
          <p className="text-lg font-bold text-blue-600">R{trend.forecast.toLocaleString()}</p>
          <p className="text-xs text-gray-500">AI Forecast</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-center">
        <div>
          <p className={`text-lg font-bold ${trend.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend.growthRate >= 0 ? '+' : ''}{trend.growthRate.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500">Growth Rate</p>
        </div>
        <div>
          <p className="text-lg font-bold text-purple-600">{trend.seasonalityFactor.toFixed(2)}x</p>
          <p className="text-xs text-gray-500">Seasonality</p>
        </div>
      </div>
    </div>
  );
}

function ExpenseCategorizationCard({ categorization }: { categorization: ExpenseCategorization }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-teal-600" />
          <h4 className="font-medium text-gray-900 dark:text-white text-sm">{categorization.description}</h4>
        </div>
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          R{categorization.amount.toLocaleString()}
        </span>
      </div>
      
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">Suggested Category</span>
          <span className="text-xs text-gray-500">{Math.round(categorization.confidence * 100)}% confidence</span>
        </div>
        <div className="p-2 bg-teal-50 dark:bg-teal-900/30 rounded-lg">
          <p className="text-sm font-semibold text-teal-700 dark:text-teal-300">{categorization.suggestedCategory}</p>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-2">
          <div 
            className="h-full bg-teal-500 rounded-full"
            style={{ width: `${categorization.confidence * 100}%` }}
          />
        </div>
      </div>

      {categorization.alternativeCategories.length > 0 && (
        <div className="mb-3">
          <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Alternative Categories</h5>
          <div className="space-y-2">
            {categorization.alternativeCategories.map((alt, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-700 dark:text-gray-300">{alt.category}</span>
                <span className="text-gray-500">{Math.round(alt.confidence * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-1">
          <Lightbulb className="w-3 h-3 flex-shrink-0 mt-0.5" />
          {categorization.reasoning}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AIERPAssistant({ 
  permissions, 
  contextInvoiceId, 
  contextContactId,
  activeSecondaryTab,
  contextDocument,
  embedded = false,
  hideEmbeddedHeader = false,
  onClose,
  initialPrompt,
  floatingPanel = false,
}: AIERPAssistantProps) {
  const { user } = useAuth();
  const { moduleColor } = useModuleColor('total-finance');
  
  const [historyOpen, setHistoryOpen] = useState(false);
  const [chatInitialPrompt, setChatInitialPrompt] = useState(initialPrompt || '');
  const containerRef = useRef<HTMLDivElement>(null);
  const [semanticContext, setSemanticContext] = useState<{
    similarDocuments?: Array<{ work_item_id: number; vendor_name: string | null; total_amount: number | null; document_date: string | null; similarity: number }>;
    similarTransactions?: Array<{ bank_transaction_id: string; embedded_text: string; assigned_category_name: string | null; similarity: number }>;
  }>({});
  const semanticContextRef = useRef(semanticContext);
  useEffect(() => { semanticContextRef.current = semanticContext; }, [semanticContext]);
  const contextDocumentRef = useRef(contextDocument);
  useEffect(() => { contextDocumentRef.current = contextDocument; }, [contextDocument]);

  const assistantProfile = useMemo(() => buildFinanceAssistantProfile({
    activeSecondaryTab,
    contextDocument,
  }), [activeSecondaryTab, contextDocument]);

  const assistantContextPayload = useMemo(() => {
    const normalizedDocument = contextDocument ? {
      id: contextDocument.documentId || undefined,
      doc_number: contextDocument.documentNumber || undefined,
      doc_type: contextDocument.documentType,
      title: contextDocument.title || undefined,
      status: contextDocument.status || undefined,
      payment_status: contextDocument.paymentStatus || undefined,
      contact_id: contextDocument.contactId ?? undefined,
      contact_name: contextDocument.contactName || undefined,
      contact_email: contextDocument.contactEmail || undefined,
      currency: contextDocument.currency || undefined,
      issued_at: contextDocument.issuedAt || undefined,
      due_date: contextDocument.dueDate || undefined,
      location_name: contextDocument.locationName || undefined,
      contact_person_name: contextDocument.contactPersonName || undefined,
      subtotal: contextDocument.subtotal ?? undefined,
      tax_total: contextDocument.taxTotal ?? undefined,
      grand_total: contextDocument.grandTotal ?? undefined,
      amount_paid: contextDocument.amountPaid ?? undefined,
      balance_due: contextDocument.balanceDue ?? undefined,
      notes: truncateText(contextDocument.notes),
      internal_notes: truncateText(contextDocument.internalNotes),
      parent_document_type: contextDocument.parentDocumentType || undefined,
      parent_document_number: contextDocument.parentDocumentNumber || undefined,
      available_promotions: (contextDocument.availablePromotions || []).map((target) => ({
        type: target.type,
        label: target.label,
      })),
      attention_flags: (contextDocument.attentionFlags || []).slice(0, 8),
      anomaly_flags: (contextDocument.anomalyFlags || []).slice(0, 8),
      risk_flags: (contextDocument.riskFlags || []).slice(0, 10),
      risk_summary: contextDocument.riskSummary || undefined,
      has_blocking_risk: contextDocument.hasBlockingRisk || false,
      linked_purchase_order_id: contextDocument.linkedPurchaseOrderId || undefined,
      linked_purchase_order_number: contextDocument.linkedPurchaseOrderNumber || undefined,
      three_way_match_status: contextDocument.threeWayMatchStatus || undefined,
      exchange_rate: contextDocument.exchangeRate ?? undefined,
      base_amount: contextDocument.baseAmount ?? undefined,
      exchange_rate_date: contextDocument.exchangeRateDate || undefined,
      policy_violations: (contextDocument.policyViolations || []).slice(0, 8),
      semantic_duplicates: contextDocument.semanticDuplicates || undefined,
      line_item_count: contextDocument.lineItemCount ?? contextDocument.lineItems?.length ?? 0,
      line_items: (contextDocument.lineItems || [])
        .slice(0, 12)
        .map((item) => ({
          description: truncateText(item.description, 120) || '',
          quantity: item.quantity ?? undefined,
          unit_price: item.unitPrice ?? undefined,
          line_total: item.lineTotal ?? undefined,
          tax_rate: item.taxRate ?? undefined,
          line_type: item.lineType ?? undefined,
          item_code: item.itemCode ?? undefined,
        }))
        .filter((item) => item.description || item.line_total !== undefined),
    } : undefined;

    return {
      invoiceId: normalizedDocument?.doc_type === 'invoice'
        ? normalizedDocument.id
        : contextInvoiceId || undefined,
      documentId: normalizedDocument?.id,
      documentNumber: normalizedDocument?.doc_number,
      documentType: normalizedDocument?.doc_type,
      contactId: normalizedDocument?.contact_id ?? contextContactId ?? undefined,
      contactName: normalizedDocument?.contact_name,
      activeFinanceView: activeSecondaryTab || undefined,
      source: embedded ? 'finance-document-modal' : 'finance-module',
      document: normalizedDocument,
      semanticContext: Object.keys(semanticContext).length > 0 ? semanticContext : undefined,
    };
  }, [activeSecondaryTab, contextContactId, contextDocument, contextInvoiceId, embedded, semanticContext]);

  const sessionScopeKey = useMemo(() => [
    embedded ? 'embedded' : 'page',
    contextDocument?.documentId || contextDocument?.documentNumber || contextInvoiceId || '',
    contextDocument?.documentType || 'finance',
    contextDocument?.contactId || contextContactId || '',
    activeSecondaryTab || '',
  ].join('::'), [
    embedded,
    contextDocument?.documentId,
    contextDocument?.documentNumber,
    contextDocument?.documentType,
    contextDocument?.contactId,
    contextInvoiceId,
    contextContactId,
    activeSecondaryTab,
  ]);


  useEffect(() => {
    setChatInitialPrompt(initialPrompt || '');
  }, [initialPrompt]);

  const {
    messages,
    setMessages,
    isGenerating,
    setIsGenerating,
    sessionId,
    credits,
    newChat: hookNewChat,
    feedback,
    applyAction: hookApplyAction,
    loadSession,
    semanticContext: hookSemanticContext,
    playbookContext: hookPlaybookContext,
  } = useAIAssistant({
    moduleKey: 'total-finance',
    moduleContext: 'finance',
    contextPayload: assistantContextPayload,
    sessionScopeKey,
    enableSemanticContext: true,
  });

  // Stable key for semantic context — only refetch when document identity changes, not on every form edit
  const semanticContextKey = useMemo(() => [
    contextDocument?.documentId || '',
    contextDocument?.documentNumber || '',
    contextDocument?.contactName || '',
  ].join('::'), [
    contextDocument?.documentId,
    contextDocument?.documentNumber,
    contextDocument?.contactName,
  ]);

  // Fetch semantic context (similar documents + transactions) when a document context is available
  useEffect(() => {
    const doc = contextDocumentRef.current;
    if (!doc) {
      setSemanticContext({});
      return;
    }

    const semanticParts: string[] = [];
    if (doc.contactName) semanticParts.push(doc.contactName);
    if (doc.documentNumber) semanticParts.push(doc.documentNumber);
    if (doc.notes) semanticParts.push(doc.notes);
    if (doc.title) semanticParts.push(doc.title);
    if (Array.isArray(doc.lineItems)) {
      for (const item of doc.lineItems.slice(0, 5)) {
        if (item.description) semanticParts.push(item.description);
      }
    }
    const semanticQuery = semanticParts.join(' | ');
    if (!semanticQuery.trim()) {
      setSemanticContext({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) return;

        const response = await fetch('/api/v1/finance/semantic-intake', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.access_token}`,
          },
          body: JSON.stringify({
            action: 'semantic-context',
            query_text: semanticQuery,
            limit: 5,
          }),
        });

        const data = await response.json();
        if (!cancelled && data.success) {
          setSemanticContext({
            similarDocuments: data.similarDocuments,
            similarTransactions: data.similarTransactions,
          });
        }
      } catch {
        // Semantic context is optional — continue without it
      }
    })();

    return () => { cancelled = true; };
  }, [semanticContextKey]);

  const handleSendMessage = useCallback(async (messageToSend: string, files?: globalThis.File[], options?: { provider?: string }) => {
    if (!messageToSend.trim() || isGenerating) return;

    const userMessage: AIMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: messageToSend,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsGenerating(true);

    try {
      let attachments: { name: string; type: string; size: number; content_base64: string }[] | undefined;
      if (files && files.length > 0) {
        attachments = await Promise.all(
          files.map(file => new Promise<{ name: string; type: string; size: number; content_base64: string }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = (reader.result as string).split(',')[1] || '';
              resolve({ name: file.name, type: file.type, size: file.size, content_base64: base64 });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }))
        );
      }

      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/v1/ai-assistant/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          sessionId,
          message: messageToSend,
          moduleContext: 'finance',
          context: assistantContextPayload,
          semanticContext: hookSemanticContext || undefined,
          playbookContext: hookPlaybookContext || undefined,
          provider: 'total-vision',
          ...(attachments && { attachments })
        })
      });

      const data = await response.json();
      
      if (data.success) {
        let responseContent = data.response;

        const wiIds = Array.from(new Set(
          Array.from(responseContent.matchAll(/\bwi:(\d+)\b/g), (m: RegExpMatchArray) => m[1])
        ));

        if (wiIds.length > 0) {
          try {
            const tokenMap: Record<string, string> = {};
            await Promise.all(wiIds.map(async (id) => {
              try {
                const permalinkResponse = await fetch(`/api/v1/work-items/${id}/permalink`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokens.access_token}`,
                  },
                });
                const permalinkData = await permalinkResponse.json();
                if (permalinkData.success && permalinkData.token) {
                  tokenMap[id] = permalinkData.token;
                }
              } catch {
                // Permalink fetch failed — wi:ID will still render as a clickable event link
              }
            }));

            for (const [id, token] of Object.entries(tokenMap)) {
              responseContent = responseContent.replace(
                new RegExp(`\\bwi:${id}\\b`, 'g'),
                `[Work Item #${id}](/wi/${token})`
              );
            }
          } catch {
            // Permalink resolution failed — keep wi:ID format, which renders as event-triggered link
          }
        }

        const assistantMessage: AIMessage = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: responseContent,
          timestamp: new Date().toISOString(),
          attachments: data.attachments,
          actions: data.actions
        };

        setMessages(prev => [...prev, assistantMessage]);

      } else {
        throw new Error(data.error || 'Failed to get AI response');
      }
    } catch (error: unknown) {
      console.error('Chat error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate response';
      notify.error(errorMessage);
      
      setMessages(prev => [...prev, {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errorMessage}. Please try again.`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, sessionId, assistantContextPayload, hookSemanticContext, hookPlaybookContext, setMessages, setIsGenerating]);

  // Custom attachment renderer for finance-specific cards
  const renderAttachment = useCallback((attachment: AIAttachment) => {
    switch (attachment.type) {
      case 'invoice_insights': {
        const insights = Array.isArray(attachment.data) ? attachment.data : [];
        return insights.map((insight: InvoiceInsight, i: number) => (
          <InvoiceInsightCard key={`${insight.invoiceId || insight.invoiceNumber || 'invoice'}-${i}`} insight={insight} />
        ));
      }
      case 'cash_flow_summary':
        return <CashFlowCard summary={attachment.data} />;
      case 'payment_analysis': {
        const analyses = Array.isArray(attachment.data) ? attachment.data : [];
        return analyses.map((analysis: PaymentAnalysis, i: number) => (
          <PaymentAnalysisCard key={`${analysis.customerId || analysis.customerName || 'analysis'}-${i}`} analysis={analysis} />
        ));
      }
      case 'financial_kpis':
        return <FinancialKPICard kpis={attachment.data} />;
      case 'cash_flow_forecast':
        return <CashFlowForecastCard forecast={attachment.data} />;
      case 'transaction_anomaly': {
        const anomalies = Array.isArray(attachment.data) ? attachment.data : [];
        return anomalies.map((anomaly: TransactionAnomaly, i: number) => (
          <TransactionAnomalyCard key={`${anomaly.transactionId || anomaly.transactionNumber || 'anomaly'}-${i}`} anomaly={anomaly} />
        ));
      }
      case 'revenue_trend': {
        const trends = Array.isArray(attachment.data) ? attachment.data : [];
        return trends.map((trend: RevenueTrend, i: number) => (
          <RevenueTrendCard key={`${trend.period || 'trend'}-${i}`} trend={trend} />
        ));
      }
      case 'expense_categorization': {
        const categorizations = Array.isArray(attachment.data) ? attachment.data : [];
        return categorizations.map((categorization: ExpenseCategorization, i: number) => (
          <ExpenseCategorizationCard key={`${categorization.expenseId || categorization.description || 'expense'}-${i}`} categorization={categorization} />
        ));
      }
      default:
        return null;
    }
  }, []);

  const renderActions = useCallback((actions: AISuggestedAction[], onApply: (action: AISuggestedAction) => void) => {
    return <FinanceActionsCard actions={actions} onApply={onApply} />;
  }, []);

  const handleQuickAction = useCallback((actionId: string) => {
    const action = assistantProfile.quickActions.find((quickAction) => quickAction.id === actionId);
    if (!action?.prompt) {
      return;
    }

    setChatInitialPrompt(action.prompt);
  }, [assistantProfile.quickActions]);

  const handleNewChat = useCallback(() => {
    setChatInitialPrompt('');
    setHistoryOpen(false);
    hookNewChat();
  }, [hookNewChat]);

  const resolvedUserName = user?.first_name || user?.email || undefined;

  if (!permissions.can_view) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
        You do not have permission to use the finance AI assistant.
      </div>
    );
  }

  if (floatingPanel) {
    return (
      <AIAssistantChat
        title={assistantProfile.title}
        titleExtra={<UserDocsLink docsPath="/total-finance/administration#the-ai-assistant" label="User guide: The AI Assistant" />}
        subtitle={assistantProfile.subtitle}
        moduleContext="finance"
        moduleKey="total-finance"
        welcomeTitle={assistantProfile.welcomeTitle}
        welcomeMessage={assistantProfile.welcomeMessage}
        inputPlaceholder={assistantProfile.inputPlaceholder}
        userName={resolvedUserName}

        messages={messages}
        isGenerating={isGenerating}
        creditsRemaining={credits?.remaining ?? 0}
        creditsTotal={credits?.total ?? 0}
        creditsResetDate={credits?.resetDate ?? undefined}
        showCredits={false}

        quickActions={assistantProfile.quickActions}
        examplePrompts={assistantProfile.examplePrompts}
        showQuickActions={false}
        showQuickActionsInHeader={false}

        onSendMessage={handleSendMessage}
        onQuickAction={handleQuickAction}
        onNewChat={handleNewChat}
        onFeedback={feedback}
        onApplyAction={hookApplyAction}
        onClose={onClose}

        showHistory={false}

        renderAttachment={renderAttachment}
        renderActions={renderActions}

        useUnifiedTheming={true}
        primaryColor={moduleColor}
        isMinimized={false}
        minimizable={false}
        closable={Boolean(onClose)}

        useBrandedSpinner={true}
        spinnerSize="sm"
        initialPrompt={chatInitialPrompt}
        inputRows={2}
        inputMinHeightClassName="min-h-[44px]"
        inputMaxHeightClassName="max-h-[160px]"
        hideHeader={false}
        disableInitialScrollReset={true}

        floatingPanel={true}
      />
    );
  }

  if (embedded) {
    return (
      <div ref={containerRef} className="h-full flex flex-col">
        <AIAssistantChat
          title={assistantProfile.title}
          titleExtra={<UserDocsLink docsPath="/total-finance/administration#the-ai-assistant" label="User guide: The AI Assistant" />}
          subtitle={assistantProfile.subtitle}
          moduleContext="finance"
          moduleKey="total-finance"
          welcomeTitle={assistantProfile.welcomeTitle}
          welcomeMessage={assistantProfile.welcomeMessage}
          inputPlaceholder={assistantProfile.inputPlaceholder}
          userName={resolvedUserName}

          messages={messages}
          isGenerating={isGenerating}
          creditsRemaining={credits?.remaining ?? 0}
          creditsTotal={credits?.total ?? 0}
          creditsResetDate={credits?.resetDate ?? undefined}
          showCredits={false}

          quickActions={assistantProfile.quickActions}
          examplePrompts={assistantProfile.examplePrompts}
          showQuickActions={false}
          showQuickActionsInHeader={false}

          onSendMessage={handleSendMessage}
          onQuickAction={handleQuickAction}
          onNewChat={handleNewChat}
          onFeedback={feedback}
          onApplyAction={hookApplyAction}
          onClose={onClose}

          showHistory={false}

          renderAttachment={renderAttachment}
          renderActions={renderActions}

          useUnifiedTheming={true}
          primaryColor={moduleColor}
          isMinimized={false}
          minimizable={false}
          closable={Boolean(onClose)}

          useBrandedSpinner={true}
          spinnerSize="sm"
          initialPrompt={chatInitialPrompt}
          inputRows={3}
          inputMinHeightClassName="min-h-[88px] sm:min-h-[104px]"
          inputMaxHeightClassName="max-h-[240px]"
          hideHeader={hideEmbeddedHeader}
          disableInitialScrollReset={true}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      <div className="flex-1 min-h-0">
        <AIAssistantChat
          title={assistantProfile.title}
          titleExtra={<UserDocsLink docsPath="/total-finance/administration#the-ai-assistant" label="User guide: The AI Assistant" />}
          subtitle={assistantProfile.subtitle}
          moduleContext="finance"
          moduleKey="total-finance"
          welcomeTitle={assistantProfile.welcomeTitle}
          welcomeMessage={assistantProfile.welcomeMessage}
          inputPlaceholder={assistantProfile.inputPlaceholder}
          userName={resolvedUserName}
          
          messages={messages}
          isGenerating={isGenerating}
          creditsRemaining={credits?.remaining ?? 0}
          creditsTotal={credits?.total ?? 0}
          creditsResetDate={credits?.resetDate ?? undefined}
          showCredits={true}
          
          quickActions={assistantProfile.quickActions}
          examplePrompts={assistantProfile.examplePrompts}
          showQuickActions={false}
          showQuickActionsInHeader={false}
          
          onSendMessage={handleSendMessage}
          onQuickAction={handleQuickAction}
          onNewChat={handleNewChat}
          onFeedback={feedback}
          onApplyAction={hookApplyAction}
          onClose={onClose}

          showHistory={true}
          onLoadSession={loadSession}
          externalHistoryOpen={historyOpen}
          onHistoryOpenChange={setHistoryOpen}
          
          renderAttachment={renderAttachment}
          renderActions={renderActions}
          
          useUnifiedTheming={true}
          primaryColor={moduleColor}
          headerStorageKey="ta_finance_ai_assistant_header"
          isMinimized={false}
          minimizable={false}
          closable={Boolean(onClose)}
          
          useBrandedSpinner={true}
          spinnerSize="sm"
          initialPrompt={chatInitialPrompt}
          disableInitialScrollReset={false}
        />
      </div>
    </div>
  );
}
